import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PhoneAndroid from '@mui/icons-material/PhoneAndroid';
import Notifications from '@mui/icons-material/Notifications';
import IosShare from '@mui/icons-material/IosShare';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import Download from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { useTracking } from '@common/hooks/useTracking';
import useAuth from '@modules/auth/hooks/api/useAuth';
import useAccount from '@modules/account/hooks/api/useAccount';
import DiscordButton from '@common/layout/DiscordButton';
import { Any } from '@common/defs/types';
import { safeLocalStorage } from '@common/utils/storage';

// Clés localStorage pour les cooldowns
const INSTALL_ACCEPTED_KEY = 'pwa_install_accepted_at';
const INSTALL_POSTPONED_KEY = 'pwa_install_postponed_at';
const HAS_INSTALLED_PWA_KEY = 'has_installed_pwa';
const NOTIFICATION_DECLINED_KEY = 'pwa_notification_declined_at';
const NOTIFICATION_SUBSCRIBE_FAILED_KEY = 'pwa_notification_subscribe_failed_at';

// Durées de cooldown en jours
const INSTALL_POSTPONED_COOLDOWN_DAYS = 15;
const NOTIFICATION_COOLDOWN_DAYS = 15;
const NOTIFICATION_SUBSCRIBE_FAILED_COOLDOWN_DAYS = 7;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface WindowWithMSStream extends Window {
  MSStream?: unknown;
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

export type PwaContextType = {
  // Propriétés d'état
  isMobile: boolean;
  isPwa: boolean;
  hasInstalledPwa: boolean;
  isWeb: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  platform: 'ios' | 'android' | 'desktop';
  isInstallable: boolean;
  isInstalled: boolean;
  notificationPermission: NotificationPermission;
  notificationSubscription: PushSubscription | null;
  isNotificationSupported: boolean;
  isLoading: boolean;

  // Méthodes
  promptToSubToNotif: () => Promise<void>;
  promptToInstall: () => Promise<boolean>;
  showRequestInstallPopup: () => void;
  showRequestNotifPermissionPopup: () => void;
  canShowInstallPrompt: () => boolean;
};

const PwaContext = createContext<PwaContextType>(undefined!);

// Fonction utilitaire pour convertir VAPID key
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

// Détecter si on est en mode standalone (PWA installée)
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as NavigatorWithStandalone).standalone === true
  );
};

export const PwaProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const { updatePushSubscription } = useAccount();
  const { enqueueSnackbar } = useSnackbar();

  // États pour la détection PWA/Installation
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isChromeIos, setIsChromeIos] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');
  const [hasInstalledPwa, setHasInstalledPwa] = useState(false);

  // États pour les notifications
  const [isNotificationSupported, setIsNotificationSupported] = useState(false);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>('default');
  const [notificationSubscription, setNotificationSubscription] = useState<PushSubscription | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  // États pour les popups
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showAndroidInstructions, setShowAndroidInstructions] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Calculer isPwa et isWeb (recalculé à chaque render)
  const isPwa = isStandalone();
  const isWeb = !isPwa;

  // Mettre à jour hasInstalledPwa si on détecte qu'on est en mode standalone
  useEffect(() => {
    if (isPwa && !hasInstalledPwa) {
      trackEvent('pwa.request_install_installed');
      setHasInstalledPwa(true);
      safeLocalStorage.setItem(HAS_INSTALLED_PWA_KEY, 'true');
    }
  }, [isPwa, hasInstalledPwa]);

  // Initialisation : Détection du device et PWA
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const ua = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !(window as WindowWithMSStream).MSStream;
    const isAndroidDevice = /Android/.test(ua);
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      ua
    );
    // Détecter Chrome sur iOS (utilise "CriOS" dans le user agent)
    const isChromeIOSDevice = isIOSDevice && /CriOS/.test(ua);

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    setIsChromeIos(isChromeIOSDevice);
    setIsMobile(isMobileDevice);

    if (isIOSDevice) {
      setPlatform('ios');
    } else if (isAndroidDevice) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    const standalone = isStandalone();
    setIsInstalled(standalone);

    // Vérifier localStorage pour hasInstalledPwa
    const hasInstalledPwaFromStorage = safeLocalStorage.getItem(HAS_INSTALLED_PWA_KEY) === 'true';
    if (standalone) {
      setHasInstalledPwa(true);
      safeLocalStorage.setItem(HAS_INSTALLED_PWA_KEY, 'true');
    } else if (hasInstalledPwaFromStorage) {
      setHasInstalledPwa(true);
    }

    if (standalone) {
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      setHasInstalledPwa(true);
      safeLocalStorage.setItem(HAS_INSTALLED_PWA_KEY, 'true');
      safeLocalStorage.setItem(INSTALL_ACCEPTED_KEY, new Date().toISOString());
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);
  // Initialisation : Notifications
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const initialize = async () => {
      const checkSupport =
        'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
      setIsNotificationSupported(checkSupport);

      if (checkSupport) {
        setNotificationPermission(Notification.permission);
        await loadSubscription();
      }

      setIsLoading(false);
    };

    const loadSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        setNotificationSubscription(existingSubscription);
      } catch (error) {
        console.error('Error loading push subscription:', error);
      }
    };

    initialize();
  }, []);

  const subscribeToPush = useCallback(
    async (forceUpdate: boolean = false): Promise<PushSubscription | null> => {
      if (!isNotificationSupported || notificationPermission !== 'granted') {
        return null;
      }

      // Vérifier le cooldown des échecs de souscription (sauf si forceUpdate)
      if (!forceUpdate) {
        const lastFailedAt = safeLocalStorage.getItem(NOTIFICATION_SUBSCRIBE_FAILED_KEY);
        if (lastFailedAt) {
          const failedDate = new Date(lastFailedAt);
          const daysSinceFailed = (Date.now() - failedDate.getTime()) / (1000 * 60 * 60 * 24);

          if (daysSinceFailed < NOTIFICATION_SUBSCRIBE_FAILED_COOLDOWN_DAYS) {
            console.log(
              'Skipping push subscription attempt due to recent failure (cooldown active)'
            );
            return null;
          }
        }
      }

      try {
        const registration = await navigator.serviceWorker.ready;

        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription && !forceUpdate) {
          setNotificationSubscription(existingSubscription);
          return existingSubscription;
        }

        const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
        if (!vapidPublicKey) {
          console.error('VAPID public key not found');
          return null;
        }
        const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

        // Timeout de 3 secondes pour détecter si le navigateur bloque les notifications
        const timeoutPromise = new Promise<null>((resolve) => {
          setTimeout(() => {
            console.error('Push subscription timeout - browser may be blocking notifications');
            resolve(null);
          }, 3000);
        });

        const subscriptionPromise = registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey.buffer.slice(0) as Any,
        });

        const newSubscription = await Promise.race([subscriptionPromise, timeoutPromise]);

        // Si timeout (newSubscription est null), afficher un message d'erreur
        if (!newSubscription) {
          // Stocker la date d'échec pour éviter de réessayer trop souvent
          safeLocalStorage.setItem(NOTIFICATION_SUBSCRIBE_FAILED_KEY, new Date().toISOString());

          enqueueSnackbar(
            'Une erreur est survenue : votre navigateur est configuré pour ne pas accepter les notifications (ex : bloqueur de pub, VPN...)',
            {
              variant: 'error',
              autoHideDuration: 8000,
            }
          );
          return null;
        }

        // Succès : supprimer la clé d'échec si elle existait
        safeLocalStorage.removeItem(NOTIFICATION_SUBSCRIBE_FAILED_KEY);
        setNotificationSubscription(newSubscription);

        const response = await updatePushSubscription(newSubscription);
        if (response.success) {
          trackEvent('push_notification_enabled', {
            userId: user?.id,
            userEmail: user?.email,
            endpoint: newSubscription.endpoint,
            timestamp: new Date().toISOString(),
          });
          enqueueSnackbar('Notifications activées', { variant: 'success' });
        }
        return newSubscription;
      } catch (error) {
        console.error('Error subscribing to push notifications:', error);
        return null;
      }
    },
    [
      isNotificationSupported,
      notificationPermission,
      updatePushSubscription,
      user,
      trackEvent,
      enqueueSnackbar,
    ]
  );

  // Auto-subscribe quand permission accordée
  useEffect(() => {
    if (notificationPermission === 'granted') {
      subscribeToPush();
    }
  }, [notificationPermission, subscribeToPush]);

  const promptToSubToNotif = useCallback(async (): Promise<void> => {
    if (!isNotificationSupported) {
      return;
    }

    try {
      setIsProcessing(true);
      // Vérifier d'abord si la permission est déjà définie
      let result: NotificationPermission;
      if (Notification.permission === 'default') {
        // Timeout de 3 secondes pour détecter le mode quiet d'Edge
        // En mode quiet, requestPermission() ne déclenche pas la popup et se bloque
        const timeoutPromise = new Promise<NotificationPermission>((resolve) => {
          setTimeout(() => {
            console.log('Permission request timeout - likely in Edge quiet mode');
            resolve('default');
          }, 5000);
        });

        const permissionPromise = Notification.requestPermission();
        result = await Promise.race([permissionPromise, timeoutPromise]);

        // Si timeout (result est toujours 'default' après 3s), on est probablement en mode quiet
        if (result === 'default') {
          // Informer l'utilisateur de cliquer sur l'icône dans la barre d'adresse
          enqueueSnackbar(
            "Veuillez cliquer sur l'icône 🔔 dans la barre d'adresse pour activer les notifications",
            {
              variant: 'info',
              autoHideDuration: 8000,
            }
          );
          trackEvent('pwa.request_notif_quiet_mode_detected');
          setShowNotificationDialog(false);
          setIsProcessing(false);
          return;
        }
      } else {
        result = Notification.permission;
      }

      setNotificationPermission(result);

      if (result === 'granted') {
        trackEvent('pwa.request_notif_accepted');
        setShowNotificationDialog(false);
      } else if (result === 'denied') {
        trackEvent('pwa.request_notif_denied');
        safeLocalStorage.setItem(NOTIFICATION_DECLINED_KEY, new Date().toISOString());
        setShowNotificationDialog(false);
      } else {
        // result === 'default' (dismissed)
        trackEvent('pwa.request_notif_dismissed');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [isNotificationSupported, trackEvent, enqueueSnackbar]);

  const promptToInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        setIsInstallable(false);
        setIsInstalled(true);
        setDeferredPrompt(null);
        setHasInstalledPwa(true);
        safeLocalStorage.setItem(HAS_INSTALLED_PWA_KEY, 'true');
        safeLocalStorage.setItem(INSTALL_ACCEPTED_KEY, new Date().toISOString());

        if (user) {
          trackEvent('pwa.request_install_installed', {
            userId: user.id,
            userEmail: user.email,
            timestamp: new Date().toISOString(),
          });
        }
        enqueueSnackbar('Application installée', { variant: 'success' });
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error prompting install:', error);
      return false;
    }
  }, [deferredPrompt, user, trackEvent, enqueueSnackbar]);

  const showRequestInstallPopup = useCallback(() => {
    trackEvent('pwa.request_install_page_view');
    setShowInstallDialog(true);
  }, []);

  const showRequestNotifPermissionPopup = useCallback(() => {
    setShowNotificationDialog(true);
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (isAndroid || isMobile) {
      setIsProcessing(true);

      try {
        if (deferredPrompt && isInstallable) {
          const accepted = await promptToInstall();
          if (accepted) {
            setShowInstallDialog(false);
            setShowIOSInstructions(false);
            setShowAndroidInstructions(false);
          }
        } else {
          setShowAndroidInstructions(true);
        }
      } catch (error) {
        console.error('Error during programmatic installation:', error);
        setShowAndroidInstructions(true);
      } finally {
        setIsProcessing(false);
      }
    }
  }, [isIOS, isAndroid, isMobile, deferredPrompt, isInstallable, promptToInstall]);

  // Fonction pour vérifier si on peut afficher le prompt d'installation
  const canShowInstallPrompt = useCallback((): boolean => {
    // Ne jamais afficher le prompt sur Chrome iOS
    if (isChromeIos) {
      return false;
    }

    // Ne pas afficher si déjà installé
    if (hasInstalledPwa) {
      return false;
    }

    const postponedAt = safeLocalStorage.getItem(INSTALL_POSTPONED_KEY);
    if (!postponedAt) {
      return true;
    }

    const postponedDate = new Date(postponedAt);
    const daysSincePostponed = (Date.now() - postponedDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysSincePostponed >= INSTALL_POSTPONED_COOLDOWN_DAYS;
  }, [isChromeIos, hasInstalledPwa]);

  // Fonction pour vérifier si on peut afficher le prompt de notifications
  const canShowNotificationPrompt = useCallback((): boolean => {
    if (notificationSubscription || notificationPermission === 'granted') {
      return false;
    }

    const declinedAt = safeLocalStorage.getItem(NOTIFICATION_DECLINED_KEY);
    if (!declinedAt) {
      return true;
    }

    const declinedDate = new Date(declinedAt);
    const daysSinceDeclined = (Date.now() - declinedDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysSinceDeclined >= NOTIFICATION_COOLDOWN_DAYS;
  }, [notificationSubscription, notificationPermission]);
  // Déclenchement automatique des popups selon les règles métier
  useEffect(() => {
    if (isLoading || isProcessing) {
      return;
    }

    // Ne pas afficher les popups sur les pages d'authentification
    // const isLandingPage = router.pathname === '/lp' || router.pathname === '/';

    // const canShowInstall = canShowInstallPrompt();
    const canShowNotif = canShowNotificationPrompt();

    // 2. Popup d'installation (NON bloquante) : si mobile ET web ET pas sur landing page ET PWA pas installée
    // Afficher le popup d'installation sur toutes les pages sauf /lp
    // Ne pas afficher sur Chrome iOS
    // if (isMobile && isWeb && !isLandingPage && !showInstallDialog && !isPwa && canShowInstall) {
    //   setShowInstallDialog(true);
    //   trackEvent('pwa.request_install_page_view');
    //   return;
    // }

    // 3. Popup notifications : si onboarding complété ET notifications pas activées
    // Se réaffiche tant que pas accepté/refusé (avec respect du cooldown de 15 jours)
    // Fonctionne sur web ET PWA (les notifications push sont supportées sur les deux)
    if (
      isNotificationSupported &&
      user &&
      user.isOnboardingCompleted === true &&
      notificationPermission !== 'granted' &&
      !notificationSubscription &&
      canShowNotif &&
      !showNotificationDialog &&
      !showInstallDialog
    ) {
      setShowNotificationDialog(true);
      trackEvent('pwa.request_notif_page_view');
    }
  }, [
    isLoading,
    isProcessing,
    isMobile,
    isWeb,
    isPwa,
    hasInstalledPwa,
    user,
    isNotificationSupported,
    notificationPermission,
    notificationSubscription,
    canShowInstallPrompt,
    canShowNotificationPrompt,
    showInstallDialog,
    showNotificationDialog,
    router.pathname,
    router,
    trackEvent,
  ]);

  const value = useMemo(
    () => ({
      isMobile,
      isPwa,
      hasInstalledPwa,
      isWeb,
      isIOS,
      isAndroid,
      platform,
      isInstallable,
      isInstalled,
      notificationPermission,
      notificationSubscription,
      isNotificationSupported,
      isLoading,
      promptToSubToNotif,
      promptToInstall,
      showRequestInstallPopup,
      showRequestNotifPermissionPopup,
      canShowInstallPrompt,
    }),
    [
      isMobile,
      isPwa,
      hasInstalledPwa,
      isWeb,
      isIOS,
      isAndroid,
      platform,
      isInstallable,
      isInstalled,
      notificationPermission,
      notificationSubscription,
      isNotificationSupported,
      isLoading,
      promptToSubToNotif,
      promptToInstall,
      showRequestInstallPopup,
      showRequestNotifPermissionPopup,
      canShowInstallPrompt,
    ]
  );
  return (
    <PwaContext.Provider value={value}>
      {children}

      {/* Popup iOS Instructions */}
      <Dialog
        open={showIOSInstructions}
        onClose={() => {
          setShowIOSInstructions(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneAndroid color="primary" />
            <Typography variant="h6">Installer sur votre téléphone</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 12,
                  mt: 0.3,
                }}
              >
                1
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontSize={13}>
                  Appuyez sur le bouton Partager
                </Typography>
                <IosShare sx={{ fontSize: 16 }} />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 12,
                  mt: 0.3,
                }}
              >
                2
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontSize={13}>
                  Sélectionnez "Ajouter à l'écran d'accueil"
                </Typography>
                <AddCircleOutline sx={{ fontSize: 16 }} />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 12,
                  mt: 0.3,
                }}
              >
                3
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontSize={13}>
                  Confirmez en appuyant sur "Ajouter"
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <DiscordButton text="Demander de l'aide" />
        </DialogActions>
      </Dialog>

      {/* Popup Android Instructions */}
      <Dialog
        open={showAndroidInstructions}
        onClose={() => {
          setShowAndroidInstructions(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneAndroid color="primary" />
            <Typography variant="h6">Installer sur votre téléphone</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 12,
                  mt: 0.3,
                }}
              >
                1
              </Box>
              <Box>
                <Typography variant="body2">
                  Appuyez sur le menu (⋮) en haut à droite dans votre navigateur
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 12,
                  mt: 0.3,
                }}
              >
                2
              </Box>
              <Box>
                <Typography variant="body2">
                  Sélectionnez "Installer l'application" ou "Ajouter à l'écran d'accueil"
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 12,
                  mt: 0.3,
                }}
              >
                3
              </Box>
              <Box>
                <Typography variant="body2">Confirmez l'installation</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <DiscordButton text="Demander de l'aide" />
        </DialogActions>
      </Dialog>

      {/* Popup Installation (non bloquante) */}
      <Dialog
        open={showInstallDialog}
        onClose={() => {
          setShowInstallDialog(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneAndroid color="primary" />
            <Typography variant="h6">Installer l'application</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pour accéder à l'intégralité des fonctionnalités, installez l'application.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              trackEvent('pwa.request_install_postponed');
              safeLocalStorage.setItem(INSTALL_POSTPONED_KEY, new Date().toISOString());
              setShowInstallDialog(false);

              // Rediriger vers auth si l'utilisateur n'est pas connecté
              if (!user) {
                router.push({
                  pathname: '/auth',
                  query: router.query,
                });
              }
            }}
            variant="text"
            color="inherit"
          >
            Plus tard
          </Button>
          <Button
            onClick={handleInstallClick}
            variant="contained"
            color="primary"
            disabled={isProcessing}
            startIcon={<Download />}
          >
            Installer maintenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup Notifications */}
      <Dialog
        open={showNotificationDialog}
        onClose={() => {
          trackEvent('pwa.request_notif_dismissed');
          safeLocalStorage.setItem(NOTIFICATION_DECLINED_KEY, new Date().toISOString());
          setShowNotificationDialog(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Notifications color="primary" />
            <Typography variant="h6">Dernière étape : activez les notifications</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pour être informé en premier des nouveaux prospects qui souhaitent une séance.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
            <Typography variant="caption" display="block">
              <strong>Astuce :</strong> Si la popup de permission ne s'affiche pas, cliquez sur
              l'icône 🔔 dans la barre d'adresse de votre navigateur.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              trackEvent('pwa.request_notif_dismissed');
              safeLocalStorage.setItem(NOTIFICATION_DECLINED_KEY, new Date().toISOString());
              setShowNotificationDialog(false);
            }}
            color="inherit"
          >
            Plus tard
          </Button>
          <LoadingButton
            onClick={promptToSubToNotif}
            variant="contained"
            color="primary"
            loading={isProcessing}
          >
            Activer
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </PwaContext.Provider>
  );
};

export const usePwaContext = () => {
  return useContext(PwaContext);
};
